import { Controller } from "@hotwired/stimulus"
import ValueParser from '../utils/value_parser'
import { parseDate } from '../helpers/date_helpers'
import { RangeFieldsModel } from '../configs/range_fields_model'

export default class extends Controller {
  static targets = ['unit', 'range', 'period', 'comparison']

  static values = {
    mode: { type: String, default: 'preset' },
    presetRanges: { type: Object, default: {} },
    followCount: { type: Number, default: 0 }
  }

  static classes = ['custom', 'preset']

  connect() {
    window.app.scenarioForm.main = this

    app.configRanges ||= []
    app.configRanges.push(this)

    this.rememberPeriod(this.period)
    this.rememberComparison(this.comparison)
    this.setModeAppearance()
  }

  modeValueChanged() {
    this.setModeAppearance()
  }

  validate(event) {
    const ok = this.rangeTargets.every((el) => {
      const range = this.application.getControllerForElementAndIdentifier(el, 'scenarios--range')
      return range ? range.validateForSubmit() : true
    })

    if (!ok) event.preventDefault()
  }

  changeMode(_event) {
    if (this.modeValue == 'preset') {
      this.modeValue = 'custom'
      this.setPresetToCustom()
    } else {
      this.modeValue = 'preset'
      this.setPresetToOriginal()
      this.applyPresetRanges()
    }

    this.syncRangePeriods(this.period)
    this.dispatch("modeChange", { detail: { mode: this.modeValue } })
    this.setModeAppearance()
  }

  change(event) {
    const fromComparison = event?.target === this.comparisonTarget

    this.rememberPeriod(this.period)
    this.rememberComparison(this.comparison)

    // A real period selection leaves custom even if comparison is still "custom".
    if (this.period !== 'custom' && !fromComparison) {
      this.modeValue = 'preset'
      this.restoreComparison()
      this.applyPresetRanges()
    } else if (this.period === 'custom' && fromComparison && this.comparison !== 'custom') {
      this.modeValue = 'custom'
      this.setCustomComparison()
    } else if (this.period === 'custom' || this.comparison === 'custom') {
      this.modeValue = 'custom'
      this.setPresetToCustom()
      this.syncRangePeriods('custom')
    } else {
      this.modeValue = 'preset'
      this.applyPresetRanges()
    }

    this.setModeAppearance()
    this.dispatch("modeChange", { detail: { mode: this.modeValue } })
  }

  rememberPeriod(period) {
    if (period && period !== 'custom') {
      this.originalPeriod = period
    }
  }

  rememberComparison(comparison) {
    if (comparison && comparison !== 'custom') {
      this.originalComparison = comparison
    }
  }

  syncRangePeriods(period) {
    this.rangeTargets.forEach((t) => {
      t.dataset["scenarios-RangePeriodValue"] = period
    })
  }

  setModeAppearance() {
    if (this.modeValue == "custom") {
      this.element.classList.remove(...this.presetClasses)
      this.element.classList.add(...this.customClasses)
    } else {
      this.element.classList.remove(...this.customClasses)
      this.element.classList.add(...this.presetClasses)
    }
  }

  setPresetToCustom() {
    this.rememberPeriod(this.period)
    this.rememberComparison(this.comparison)
    this.periodTarget.value = 'custom'
    this.setComparisonToCustom()
  }

  setPresetToOriginal() {
    this.periodTarget.value = this.originalPeriod || 't12m'
    this.restoreComparison()
  }

  setComparisonToCustom() {
    this.setComparisonValue('custom')
  }

  restoreComparison() {
    this.setComparisonValue(this.originalComparison || 'year')
  }

  setComparisonValue(value) {
    if (!this.hasComparisonTarget) return

    this.comparisonTarget.value = value
  }

  applyPresetRanges() {
    const rangeData = this.presetRangesValue?.[this.period]?.[this.comparison]
    const suggestedUnit = this.presetRangesValue?.[this.period]?.unit

    if (!rangeData) return

    this.rangeTargets.forEach((t) => {
      const pos = ValueParser.castNumber(t.dataset.position)
      const entry = rangeData[pos]

      if (entry) {
        t.dataset["scenarios-RangeDateMinValue"] = entry.min
        t.dataset["scenarios-RangeDateMaxValue"] = entry.max
        t.dataset["scenarios-RangePeriodValue"] = this.period
      }
    })

    this.selectGroupingRadioFor(suggestedUnit)
  }

  selectGroupingRadioFor(unit) {
    if (!unit) return

    const radio = this.unitTargets.find((target) => target.value === unit)
    if (radio) radio.checked = true
  }

  setCustomComparison() {
    const rangeData = this.customRangeData

    if (!rangeData) return
    this.comparisonRange.dataset["scenarios-RangeDateMinValue"] = rangeData[2].min
    this.comparisonRange.dataset["scenarios-RangeDateMaxValue"] = rangeData[2].max
    this.comparisonRange.dataset["scenarios-RangePeriodValue"] = this.period
  }

  get customRangeData() {
    return new RangeFieldsModel({
      mode: this.modeValue,
      period: this.period,
      comparison: this.comparison,
      presetData: { map: this.presetRangesValue },
      leaderRange: this.period === 'custom' ? [
        parseDate(this.leaderRange.dataset["scenarios-RangeDateMinValue"]),
        parseDate(this.leaderRange.dataset["scenarios-RangeDateMaxValue"])
      ] : undefined
    }).selectedPeriodRanges
  }

  get period() {
    return this.periodTarget.value
  }

  get comparison() {
    return this.comparisonTargets[0].value
  }

  get comparisonRange() {
    return this.rangeTargets.find(target => target.dataset.position === '2')
  }

  get leaderRange() {
    return this.rangeTargets.find(target => target.dataset.position === '1')
  }

  setUnit(unit) {
    this.setRadio(unit)
  }

  setRadio(unit) {
    this.unitTargets.forEach(rad => {
      rad.checked = (rad.value == unit)
    })
  }
}
